import {CustomPropertyInput, ResetDropdown} from '@github-ui/custom-properties-editing'
import type {PropertyField} from '@github-ui/custom-properties-editing/use-edit-custom-properties'
import type {PropertyDefinitionBase, PropertySourceDetails, PropertyValue} from '@github-ui/custom-properties-types'
import {isEmptyPropertyValue} from '@github-ui/custom-properties-types/helpers'
import {enterprisePath, ownerPath} from '@github-ui/paths'
import {ShieldLockIcon} from '@primer/octicons-react'
import {FormControl, Link, Stack, Token, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import type {PropsWithChildren, ReactElement, ReactNode, RefCallback} from 'react'

import {getControlLabel, getReadOnlyValue} from '../../helpers/values-table'
import styles from './PropertiesValuesTableRows.module.css'

interface EditValueRowProps<T extends PropertyDefinitionBase> {
  propertyField: PropertyField | undefined
  definition: T
  showUndo?: boolean
  fetchAllowedValuesUrl?: (propertyName: string) => string
  onChange: (name: string, value: PropertyValue) => void
  onReset: (name: string) => void
  setRef?: RefCallback<HTMLElement>
}

export function EditValueRow<T extends PropertyDefinitionBase>({...props}: EditValueRowProps<T>) {
  const {propertyField, definition, showUndo, fetchAllowedValuesUrl, setRef} = props
  const {value, changed = false, mixed = false, error} = propertyField || {}
  const {propertyName, description} = definition

  const propertyNameValidationId = `${propertyName}-validation-error`

  // This is not strictly necessary, but it simplifies consuming it
  const onChange = (newValue: PropertyValue) => props.onChange(propertyName, newValue)
  const onReset = () => props.onReset(propertyName)

  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        label={propertyName}
        description={description}
        value={
          <>
            <div className={styles.controlsContainer}>
              <CustomPropertyInput
                setRef={setRef}
                {...definition}
                propertyValue={value}
                mixed={mixed}
                onChange={onChange}
                inputProps={error ? {'aria-describedby': propertyNameValidationId} : {}}
                fetchAllowedValuesUrl={fetchAllowedValuesUrl}
              />
            </div>
            {error && (
              <FormControl.Validation
                variant="error"
                id={propertyNameValidationId}
                className={styles.FormControl_Validation}
              >
                {error}
              </FormControl.Validation>
            )}
          </>
        }
        trailing={<ResetDropdown definition={definition} {...{changed, onReset, onChange, showUndo}} />}
      />
    </PropertyValueRowContainer>
  )
}

EditValueRow.displayName = 'EditValueRow'

interface FormReadRowProps<T extends PropertyDefinitionBase> {
  definition: T
  propertyField: PropertyField | undefined
}

export function FormReadValueRow<T extends PropertyDefinitionBase>({propertyField, definition}: FormReadRowProps<T>) {
  const {description, propertyName} = definition
  const readOnlyValue = getReadOnlyValue(definition, propertyField?.value)

  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        label={propertyName}
        description={description}
        value={
          <Stack direction="horizontal" gap="condensed">
            <Stack.Item shrink={false}>
              <ShieldLockIcon size="small" />
            </Stack.Item>
            <Stack.Item>
              <span>{readOnlyValue}</span>
            </Stack.Item>
          </Stack>
        }
      />
    </PropertyValueRowContainer>
  )
}

interface ReadRowProps<T extends PropertyDefinitionBase> {
  definition: T
  propertyField: PropertyField | undefined
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
}

export function ReadValueRow<T extends PropertyDefinitionBase>({
  propertyField,
  definition,
  managedBySource,
}: ReadRowProps<T>) {
  const {description} = definition

  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        label={getControlLabel(definition)}
        description={description}
        value={<DisplayPropertyValue propertyValue={propertyField?.value} definition={definition} />}
        bottomMessage={
          managedBySource && (
            <div className="d-inline">
              <span className="fg-muted">
                <ShieldLockIcon size={'small'} /> Managed by{' '}
              </span>
              <Link
                inline
                href={
                  managedBySource.type === 'org'
                    ? ownerPath({owner: managedBySource.slug})
                    : enterprisePath({slug: managedBySource.slug})
                }
              >
                {managedBySource.name}
              </Link>
            </div>
          )
        }
      />
    </PropertyValueRowContainer>
  )
}

function DisplayPropertyValue({
  propertyValue,
  definition,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinitionBase
}) {
  if (isEmptyPropertyValue(propertyValue)) {
    return <span className="fg-muted">(Empty)</span>
  }

  switch (definition.valueType) {
    case 'single_select':
    case 'true_false':
      return (
        <Token
          text={
            <Truncate title={propertyValue as string} maxWidth="100%">
              {propertyValue}
            </Truncate>
          }
          className={styles.propertyToken}
        />
      )
    case 'multi_select':
      return (
        <>
          {(propertyValue as string[]).map(v => (
            <Token
              key={v}
              text={
                <Truncate title={v} maxWidth="100%">
                  {v}
                </Truncate>
              }
              className={styles.spacedPropertyToken}
            />
          ))}
        </>
      )
    case 'string':
    case 'url':
      return (
        <Truncate title={propertyValue as string} maxWidth="100%">
          {propertyValue}
        </Truncate>
      )
  }
}

export function PropertyValueRowContainer(props: PropsWithChildren<{className?: string}>) {
  return (
    <div data-testid="property-row" className={clsx(styles.propertyRow, props.className)}>
      {props.children}
    </div>
  )
}

function DataTableRow({
  label,
  description,
  bottomMessage,
  value,
  trailing,
}: {
  label: string
  description: string | null
  bottomMessage?: ReactNode
  value: ReactNode
  trailing?: ReactNode
}) {
  return (
    <FormControl className={clsx(styles.FormControl, bottomMessage && styles.hasBottomMessage)}>
      <FormControl.Label data-testid="property-name" className={styles.FormControl_Label}>
        {label}
      </FormControl.Label>
      <div className={styles.formInputArea}>{value}</div>
      {trailing ? <div className={styles.actionButtonsArea}>{trailing}</div> : null}
      {description && <FormControl.Caption className={styles.FormControl_Caption}>{description}</FormControl.Caption>}
      {bottomMessage ? <div className={styles.formMessageArea}>{bottomMessage}</div> : null}
    </FormControl>
  )
}

try{ FormReadValueRow.displayName ||= 'FormReadValueRow' } catch {}
try{ ReadValueRow.displayName ||= 'ReadValueRow' } catch {}
try{ DisplayPropertyValue.displayName ||= 'DisplayPropertyValue' } catch {}
try{ PropertyValueRowContainer.displayName ||= 'PropertyValueRowContainer' } catch {}
try{ DataTableRow.displayName ||= 'DataTableRow' } catch {}